import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useNotify,
    useRedirect,
    useRecordContext,
    Toolbar,
    SaveButton,
    useGetList,
    useDataProvider,
    FormDataConsumer,
} from 'react-admin';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField as MuiTextField,
    Tab,
    Tabs,
    Chip,
    LinearProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    Info,
    Settings,
    Warning,
    Delete,
    Assessment,
    Bolt,
    Groups,
    Power,
} from '@mui/icons-material';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: { xs: 2, sm: 3 } }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const DeleteTemplateSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const translate = useTranslate();

    const { data: loadGroups = [] } = useGetList('load-groups', {
        filter: { lmsTemplateId: record?.id },
        pagination: { page: 1, perPage: 1 },
    });

    const isInUse = loadGroups.length > 0;

    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };

    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };

    const handleConfirm = async () => {
        if (confirmText === record?.name) {
            try {
                await dataProvider.delete('lms-templates', { id: record.id });
                notify(translate('resources.lms-templates.notifications.deleted'), { type: 'success' });
                redirect('/lms-templates');
            } catch (error) {
                notify(translate('resources.lms-templates.notifications.deleteError'), { type: 'error' });
            }
            setOpen(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 0 } }}>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {isInUse
                        ? translate('resources.lms-templates.delete.hasLoadGroupsContent')
                        : translate('resources.lms-templates.delete.content')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={isInUse}
                sx={{
                    borderColor: isInUse ? '#BDBDBD' : '#F44336',
                    color: isInUse ? '#BDBDBD' : '#F44336',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        borderColor: isInUse ? '#BDBDBD' : '#D32F2F',
                        backgroundColor: isInUse ? 'transparent' : 'rgba(244,67,54,0.08)',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={isInUse
                    ? translate('resources.lms-templates.delete.hasLoadGroupsContent')
                    : translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1,
                }}>
                    <Delete sx={{ color: '#F44336' }} />
                    <Typography variant="h6">
                        {translate('resources.lms-templates.delete.title')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {translate('resources.lms-templates.delete.warning')}
                        </Alert>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {translate('resources.lms-templates.delete.confirmMessage', { name: record?.name })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {translate('resources.lms-templates.delete.typeToConfirm', { name: record?.name })}
                        </Typography>
                        <MuiTextField
                            autoFocus
                            fullWidth
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={record?.name}
                            error={confirmText !== '' && confirmText !== record?.name}
                            helperText={confirmText !== '' && confirmText !== record?.name ? translate('resources.lms-templates.delete.nameDoesNotMatch') : ''}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleClose}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={confirmText !== record?.name}
                        variant="contained"
                        sx={{
                            backgroundColor: '#F44336',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#BDBDBD',
                            },
                        }}
                    >
                        {translate('resources.lms-templates.delete.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/lms-templates')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.lms-templates.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.lms-templates.edit.title', { name: record?.name })}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {translate('resources.lms-templates.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const UsageOverviewSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    const { data: loadGroups = [], isLoading } = useGetList('load-groups', {
        filter: { lmsTemplateId: record?.id },
        pagination: { page: 1, perPage: 100 },
    });

    const totalPowerManaged = loadGroups.reduce((sum, lg) => sum + (lg.totalCapacity || 0), 0);
    const totalChargersAffected = loadGroups.reduce((sum, lg) => sum + (lg.chargerCount || 0), 0);
    const usagePercent = loadGroups.length > 0 ? 100 : 0;

    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                    <Assessment sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.lms-templates.sections.usage')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gap: { xs: 2, sm: 3 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' } }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Groups sx={{ fontSize: 20, color: '#00C853' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.lms-templates.stats.loadGroups')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {loadGroups.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.lms-templates.loadGroupsUsing')}
                        </Typography>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Bolt sx={{ fontSize: 20, color: '#FFA726' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.lms-templates.stats.totalPower')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {totalPowerManaged.toFixed(0)} kW
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.lms-templates.stats.totalPowerDesc')}
                        </Typography>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Power sx={{ fontSize: 20, color: '#1976D2' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.lms-templates.stats.chargers')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {totalChargersAffected}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.lms-templates.stats.chargersDesc')}
                        </Typography>
                    </Box>
                </Box>

                {loadGroups.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                        {translate('resources.lms-templates.warnings.inUseWarning', { count: loadGroups.length })}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

// Edit form uses nested sources (e.g. "logic.config.powerUtilizationPercent")
// that mirror the backend body shape, so we only need to strip the top-level
// body./params./query. prefix that Zod adds.
const fieldLabelKeyForEdit = (field: string): string => {
    const tail = field.split('.').pop() || field;
    const known = new Set([
        'name', 'description', 'type', 'isActive',
        'powerUtilizationPercent', 'minPowerPerCharger',
        'powerReductionStep', 'roundToNearest',
    ]);
    if (known.has(tail)) {
        return `resources.lms-templates.fields.${tail}`;
    }
    return field;
};

const mapBackendPathToEditField = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

export const LMSTemplatesEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [tabValue, setTabValue] = useState(0);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSave = (data: any) => {
        const finalData = {
            ...data,
            isActive: typeof data.isActive === 'number' ? Boolean(data.isActive) : data.isActive,
            logic: {
                algorithm: 'equal_split',
                config: {
                    powerUtilizationPercent: data.logic?.config?.powerUtilizationPercent,
                    minPowerPerCharger: data.logic?.config?.minPowerPerCharger,
                    powerReductionStep: data.logic?.config?.powerReductionStep,
                    roundToNearest: data.logic?.config?.roundToNearest,
                },
            },
        };

        return finalData;
    };

    return (
        <Edit
            title={false}
            component="div"
            actions={false}
            redirect="list"
            transform={handleSave}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.lms-templates.notifications.updated', { type: 'success' });
                    redirect('/lms-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapBackendPathToEditField(path);
                            fieldErrors[field] = translated;
                            const labelKey = fieldLabelKeyForEdit(field);
                            const label = translate(labelKey, { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <UsageOverviewSection />

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                            <Tab label={translate('resources.lms-templates.tabs.general')} />
                            <Tab label={translate('resources.lms-templates.tabs.danger')} />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Info sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.lms-templates.sections.basic')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            label={translate('resources.lms-templates.fields.name')}
                                            validate={[
                                                required(),
                                                maxLength(100, translate("resources.lms-templates.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.name')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>

                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="description"
                                            label={translate('resources.lms-templates.fields.description')}
                                            multiline
                                            rows={3}
                                            validate={[
                                                maxLength(500, translate("resources.lms-templates.validation.descriptionTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.lms-templates.helpers.description')}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            {translate('resources.lms-templates.fields.type')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Chip
                                                label={translate('resources.lms-templates.types.equal')}
                                                sx={{
                                                    backgroundColor: '#E8F5E8',
                                                    color: '#00C853',
                                                    fontWeight: 600,
                                                }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {translate('resources.lms-templates.helpers.typeCannotChange')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Settings sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.lms-templates.sections.configuration')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        {translate('resources.lms-templates.equalDistribution.description')}
                                    </Alert>

                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="logic.config.powerUtilizationPercent"
                                                label={translate('resources.lms-templates.fields.powerUtilizationPercent')}
                                                validate={[
                                                    required(),
                                                    minValue(50, translate("resources.lms-templates.validation.utilizationTooLow")),
                                                    maxValue(100, translate("resources.lms-templates.validation.utilizationTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.lms-templates.helpers.powerUtilizationPercent')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="logic.config.minPowerPerCharger"
                                                label={translate('resources.lms-templates.fields.minPowerPerCharger')}
                                                validate={[
                                                    required(),
                                                    minValue(3, translate("resources.lms-templates.validation.minPowerTooLow")),
                                                    maxValue(22, translate("resources.lms-templates.validation.minPowerTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.lms-templates.helpers.minPowerPerCharger')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="logic.config.powerReductionStep"
                                                label={translate('resources.lms-templates.fields.powerReductionStep')}
                                                validate={[
                                                    required(),
                                                    minValue(0.1, translate("resources.lms-templates.validation.stepTooLow")),
                                                    maxValue(5, translate("resources.lms-templates.validation.stepTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.lms-templates.helpers.powerReductionStep')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <SelectInput
                                                source="logic.config.roundToNearest"
                                                label={translate('resources.lms-templates.fields.roundToNearest')}
                                                choices={[
                                                    { id: 0.1, name: '0.1 kW' },
                                                    { id: 0.5, name: '0.5 kW' },
                                                    { id: 1, name: '1 kW' },
                                                ]}
                                                validate={[required()]}
                                                fullWidth
                                                helperText={translate('resources.lms-templates.helpers.roundToNearest')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>

                                    <BooleanInput
                                        source="isActive"
                                        label={translate('resources.lms-templates.fields.isActive')}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Delete sx={{ mr: 2, color: '#F44336' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.lms-templates.sections.dangerZone')}
                                    </Typography>
                                </Box>

                                <DeleteTemplateSection />
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
